import { useEffect, useRef, useState } from "react";

// Components
import { FaChevronLeft, FaChevronRight, FaPause, FaPlay } from "react-icons/fa";

// Actions
import { doGetMessagesRequest } from "src/store/context/actions";

// Types
import type { TextMessage } from "src/types";

export default function Messages() {
  const aderente_slug = "icom-qa";

  const MESSAGE_DURATION = 10_000;
  const [progress, setProgress] = useState(0);

  const progressRef = useRef(0);
  const animationFrameRef = useRef<number | null>(null);
  const lastTimestampRef = useRef<number | null>(null);

  // Messages state
  const [messagesState, setMessagesState] = useState<{
    messages: TextMessage[];
    currentIndex: number;
    isPaused: boolean;
  }>({
    messages: [],
    currentIndex: 0,
    isPaused: false,
  });

  useEffect(() => {
    if (aderente_slug) {
      doGetMessagesRequest(aderente_slug).then((result) => {
        if (result.success && result.data) {
          setMessagesState((s) => ({ ...s, messages: result.data, currentIndex: 0 }));
        }
      });
    }
  }, [aderente_slug]);

  // Messages autoplay logic
  useEffect(() => {
    if (messagesState.messages.length === 0) {
      return;
    }

    if (messagesState.isPaused) {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      lastTimestampRef.current = null;

      return;
    }

    const animate = (timestamp: number) => {
      if (lastTimestampRef.current === null) {
        lastTimestampRef.current = timestamp;
      }

      const delta = timestamp - lastTimestampRef.current;
      lastTimestampRef.current = timestamp;

      progressRef.current += delta / MESSAGE_DURATION;

      if (progressRef.current >= 1) {
        progressRef.current = 0;
        setProgress(0);

        setMessagesState((s) => ({
          ...s,
          currentIndex: s.currentIndex + 1 >= s.messages.length ? 0 : s.currentIndex + 1,
        }));

        lastTimestampRef.current = timestamp;
      } else {
        setProgress(progressRef.current * 100);
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [messagesState.isPaused, messagesState.currentIndex, messagesState.messages.length]);

  const toggleMessagesPauseHandler = () => {
    setMessagesState((s) => ({ ...s, isPaused: !s.isPaused }));
  };

  const prevMessageHandler = () => {
    progressRef.current = 0;
    setProgress(0);

    setMessagesState((s) => {
      const newIdx = s.currentIndex > 0 ? s.currentIndex - 1 : s.messages.length - 1;

      return {
        ...s,
        currentIndex: newIdx,
      };
    });
  };

  const nextMessageHandler = () => {
    progressRef.current = 0;
    setProgress(0);

    setMessagesState((s) => {
      let newIdx = s.currentIndex + 1;

      if (newIdx >= s.messages.length) {
        newIdx = 0;
      }

      return {
        ...s,
        currentIndex: newIdx,
      };
    });
  };

  if (messagesState.messages.length === 0) return null;

  return (
    <div className="flex items-center bg-[#FEF3DA] border-2 border-[#D88108] px-6 py-4 rounded-xl text-[#4A4A4A]">
      {/* Ícone */}
      <div className="mr-4 flex items-center justify-center">
        <div className="w-10 h-10 bg-transparent rounded-full flex items-center justify-center text-[#D98200] font-bold text-xl">📢</div>
      </div>

      {/* Conteúdo */}
      <div className="flex-1">
        <h2 className="text-base font-bold mb-1 text-[#D98200]">Fique por dentro!</h2>
        <p className="text-sm text-[#4A4A4A]">{messagesState.messages[messagesState.currentIndex].mensagem}</p>
      </div>

      {/* Separador */}
      <div className="vertical-hr w-[1px] h-8 bg-[#D98200] mx-6"></div>

      {/* Paginação */}
      <div className="flex items-center gap-6">
        <span className="text-sm font-bold text-[#4A4A4A]">
          {messagesState.currentIndex + 1} / {messagesState.messages.length}
        </span>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={prevMessageHandler}
            disabled={messagesState.currentIndex === 0}
            aria-label="Anterior"
            className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#FEEBBD] text-[#4A4A4A] hover:bg-[#FFE4A3] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <FaChevronLeft />
          </button>
          <button
            type="button"
            onClick={toggleMessagesPauseHandler}
            aria-label={messagesState.isPaused ? "Retomar" : "Pausar"}
            className="relative w-10 h-10 rounded-xl flex items-center justify-center bg-[#FEEBBD] text-[#4A4A4A] hover:bg-[#FFE4A3] transition-all"
          >
            <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none" viewBox="0 0 40 40">
              <rect
                x="1.5"
                y="1.5"
                width="37"
                height="37"
                rx="10"
                fill="none"
                stroke="#D98200"
                strokeWidth="2.5"
                pathLength="100"
                strokeDasharray="100"
                strokeDashoffset={100 - progress}
                className="transition-none"
              />
            </svg>
            {/* ícone pause */}
            <span className="relative z-10">{messagesState.isPaused ? <FaPlay /> : <FaPause />}</span>
          </button>
          <button
            type="button"
            onClick={nextMessageHandler}
            disabled={messagesState.currentIndex >= messagesState.messages.length - 1}
            aria-label="Próximo"
            className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#FEEBBD] text-[#4A4A4A] hover:bg-[#FFE4A3] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            {/* ícone chevron direito */}
            <FaChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
}
